import { useCallback, useState } from 'react'

import {
  FilterCriteria,
  PaginatedMenusBrandFilter,
  PaginatedMenusCustomAttributeFilter,
  PaginatedMenusCustomAttributeValueFilter
} from 'src/apollo/onlineOrdering'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { useFilterCriteria } from './FilterCriteriaContext'
import { matchByIdOrName } from './filterUtils'

type UseFilterToggleOptions = {
  staged?: boolean
}

const hasActiveFilters = (
  criteria: FilterCriteria | null,
  inStockOnly: boolean
) =>
  !!criteria?.tags?.length ||
  !!criteria?.productOptions?.length ||
  !!criteria?.customAttributes?.length ||
  !!criteria?.priceRange ||
  !!criteria?.brands?.length ||
  !!criteria?.countriesOfOrigin?.countryCodes?.length ||
  inStockOnly

// Normalized snapshot of the resulting filter state sent as the MENU_FILTER_CHANGED payload.
const filterSnapshot = (
  criteria: FilterCriteria | null,
  inStockOnly: boolean
) => ({
  tags: criteria?.tags?.map((t) => t.name ?? '') ?? [],
  productOptions:
    criteria?.productOptions?.map((o) => ({
      name: o.name,
      values: o.values
    })) ?? [],
  customAttributes:
    criteria?.customAttributes?.map((a) => ({
      id: a.id,
      name: a.name,
      values: a.values
    })) ?? [],
  priceRange: criteria?.priceRange ?? null,
  brands: criteria?.brands?.map((b) => b.name ?? b.id ?? '') ?? [],
  countriesOfOrigin: criteria?.countriesOfOrigin?.countryCodes ?? [],
  inStockOnly
})

export const useFilterToggle = (options?: UseFilterToggleOptions) => {
  const { filterCriteria, setFilterCriteria, inStockOnly, setInStockOnly } =
    useFilterCriteria()
  const { track } = useThrottledTracker()

  const trackFilter = (
    criteria: FilterCriteria | null,
    resultingInStockOnly: boolean
  ): void =>
    track(
      TrackingEvents.MENU_FILTER_CHANGED,
      filterSnapshot(criteria, resultingInStockOnly)
    )

  const [stagedCriteria, setStagedCriteria] = useState<FilterCriteria | null>(
    filterCriteria
  )
  const [stagedInStockOnly, setStagedInStockOnly] =
    useState<boolean>(inStockOnly)

  const isStaged = options?.staged ?? false
  const current = isStaged ? stagedCriteria : filterCriteria
  const currentInStockOnly = isStaged ? stagedInStockOnly : inStockOnly
  const _setCurrent = isStaged ? setStagedCriteria : setFilterCriteria
  const _setCurrentInStockOnly = isStaged
    ? setStagedInStockOnly
    : setInStockOnly
  const setCurrent = (updated: FilterCriteria) => {
    const hasAnyFilter =
      updated.tags ||
      updated.productOptions ||
      updated.customAttributes ||
      updated.priceRange ||
      updated.brands ||
      updated.countriesOfOrigin
    _setCurrent(hasAnyFilter ? updated : null)
  }

  const selectedTags = current?.tags ?? []
  const selectedProductOptions = current?.productOptions ?? []
  const selectedCustomAttributes = current?.customAttributes ?? []
  const selectedPriceRange = current?.priceRange ?? null
  const selectedBrands = current?.brands ?? []
  const selectedCountriesOfOrigin = (
    current?.countriesOfOrigin?.countryCodes ?? []
  ).map((c) => c.toUpperCase())

  const handleTagToggle = (
    guid: string | null | undefined,
    name: string | null | undefined
  ) => {
    if (guid == null || name == null) return
    const isSelected = selectedTags.some((tag) => tag.guid === guid)
    const updatedTags = isSelected
      ? selectedTags.filter((tag) => tag.guid !== guid)
      : [...selectedTags, { guid, name }]

    const updated = {
      ...current,
      tags: updatedTags.length > 0 ? updatedTags : undefined
    }

    if (!isStaged) {
      trackFilter(updated, currentInStockOnly)
    }

    setCurrent(updated)
  }

  const handleBrandToggle = (brand: PaginatedMenusBrandFilter) => {
    const isSelected = selectedBrands.some((b) => matchByIdOrName(b, brand))
    const updatedBrands = isSelected
      ? selectedBrands.filter((b) => !matchByIdOrName(b, brand))
      : [...selectedBrands, brand]

    const updated = {
      ...current,
      brands: updatedBrands.length > 0 ? updatedBrands : undefined
    }

    if (!isStaged) {
      trackFilter(updated, currentInStockOnly)
    }

    setCurrent(updated)
  }

  const handleCountryToggle = (countryCode: string) => {
    const normalized = countryCode.toUpperCase()
    const isSelected = selectedCountriesOfOrigin.includes(normalized)
    const updatedCodes = isSelected
      ? selectedCountriesOfOrigin.filter((c) => c !== normalized)
      : [...selectedCountriesOfOrigin, normalized]

    const updated = {
      ...current,
      countriesOfOrigin:
        updatedCodes.length > 0 ? { countryCodes: updatedCodes } : undefined
    }

    if (!isStaged) {
      trackFilter(updated, currentInStockOnly)
    }

    setCurrent(updated)
  }

  const handleProductOptionToggle = (optionName: string, value: string) => {
    const existingOption = selectedProductOptions.find(
      (o) => o.name === optionName
    )
    let updatedOptions

    if (existingOption) {
      const hasValue = existingOption.values.includes(value)
      const updatedValues = hasValue
        ? existingOption.values.filter((v) => v !== value)
        : [...existingOption.values, value]

      if (updatedValues.length === 0) {
        updatedOptions = selectedProductOptions.filter(
          (o) => o.name !== optionName
        )
      } else {
        updatedOptions = selectedProductOptions.map((o) =>
          o.name === optionName ? { ...o, values: updatedValues } : o
        )
      }
    } else {
      updatedOptions = [
        ...selectedProductOptions,
        { name: optionName, values: [value] }
      ]
    }

    const updated = {
      ...current,
      productOptions: updatedOptions.length > 0 ? updatedOptions : undefined
    }

    if (!isStaged) {
      trackFilter(updated, currentInStockOnly)
    }

    setCurrent(updated)
  }

  const handleCustomAttributeToggle = (
    attribute: { id?: string | null; name: string },
    value: PaginatedMenusCustomAttributeValueFilter
  ) => {
    const matchesAttribute = (
      candidateAttribute: PaginatedMenusCustomAttributeFilter
    ) => matchByIdOrName(candidateAttribute, attribute)
    const matchesValue = (
      candidateValue: PaginatedMenusCustomAttributeValueFilter
    ) => matchByIdOrName(candidateValue, value)

    const existingAttribute = selectedCustomAttributes.find(matchesAttribute)
    let updatedAttributes

    if (existingAttribute) {
      const hasValue = existingAttribute.values.some(matchesValue)
      const updatedValues = hasValue
        ? existingAttribute.values.filter((v) => !matchesValue(v))
        : [...existingAttribute.values, value]

      if (updatedValues.length === 0) {
        updatedAttributes = selectedCustomAttributes.filter(
          (a) => !matchesAttribute(a)
        )
      } else {
        updatedAttributes = selectedCustomAttributes.map((a) =>
          matchesAttribute(a) ? { ...a, values: updatedValues } : a
        )
      }
    } else {
      // Destructure to strip __typename from catalog value objects before they enter GraphQL input.
      updatedAttributes = [
        ...selectedCustomAttributes,
        {
          id: attribute.id,
          name: attribute.name,
          values: [{ id: value.id, name: value.name }]
        }
      ]
    }

    const updated = {
      ...current,
      customAttributes:
        updatedAttributes.length > 0 ? updatedAttributes : undefined
    }

    if (!isStaged) {
      trackFilter(updated, currentInStockOnly)
    }

    setCurrent(updated)
  }

  const handlePriceRangeChange = (
    min: number,
    max: number,
    bounds: { min: number; max: number }
  ) => {
    const isFullRange = min <= bounds.min && max >= bounds.max
    const nextPriceRange = isFullRange ? undefined : { min, max }
    const updated = {
      ...current,
      priceRange: nextPriceRange
    }
    if (
      !isStaged &&
      JSON.stringify(nextPriceRange ?? null) !==
        JSON.stringify(selectedPriceRange)
    ) {
      trackFilter(updated, currentInStockOnly)
    }
    setCurrent(updated)
  }

  const clearPriceRange = () => {
    const updated = { ...current, priceRange: undefined }
    if (!isStaged && selectedPriceRange) {
      trackFilter(updated, currentInStockOnly)
    }
    setCurrent(updated)
  }

  const handleInStockOnlyToggle = () => {
    const nextInStockOnly = !currentInStockOnly
    if (!isStaged) {
      trackFilter(current, nextInStockOnly)
    }
    _setCurrentInStockOnly(nextInStockOnly)
  }

  const clearAll = () => {
    // When staged (the filter panel), clearing only edits the draft; the clear is committed — and tracked — on apply().
    if (!isStaged && hasActiveFilters(filterCriteria, inStockOnly)) {
      trackFilter(null, false)
    }
    _setCurrent(null)
    _setCurrentInStockOnly(false)
  }

  const apply = () => {
    // Bail on a no-op Apply (staged unchanged from committed) so we don't inflate MENU_FILTER_CHANGED counts.
    const committed = filterSnapshot(filterCriteria, inStockOnly)
    const staged = filterSnapshot(stagedCriteria, stagedInStockOnly)
    if (JSON.stringify(committed) === JSON.stringify(staged)) return

    // Report one snapshot of the resulting state; removals are absent entries / false.
    trackFilter(stagedCriteria, stagedInStockOnly)

    setFilterCriteria(stagedCriteria)
    setInStockOnly(stagedInStockOnly)
  }

  const syncFromContext = useCallback(() => {
    setStagedCriteria(filterCriteria)
    setStagedInStockOnly(inStockOnly)
  }, [filterCriteria, inStockOnly])

  return {
    selectedTags,
    selectedProductOptions,
    selectedCustomAttributes,
    selectedPriceRange,
    selectedBrands,
    selectedCountriesOfOrigin,
    inStockOnly: currentInStockOnly,
    handleTagToggle,
    handleProductOptionToggle,
    handleCustomAttributeToggle,
    handlePriceRangeChange,
    handleInStockOnlyToggle,
    handleBrandToggle,
    handleCountryToggle,
    clearPriceRange,
    clearAll,
    // Only meaningful in staged mode; undefined otherwise so the return type stays stable.
    apply: isStaged ? apply : undefined,
    syncFromContext: isStaged ? syncFromContext : undefined
  }
}
